import React from 'react';
import { Table, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';

interface BidRowProps {
    bid: {
        id: string;
        hashId: string;
        amount: number;
        unit: string;
        bidStatus: string;
        createdAt: string;
        calculatedIrr: number | null;
        manualIrr?: number | null;
        irrSpreadsheetUrl?: string;
        project?: {
            id: string;
            name: string;
            parentId?: string;
            portfolio?: {
                id: string;
                name: string;
                parentId?: string;
            };
        };
        investor?: {
            company?: {
                name: string;
            };
        };
    };
    onEditIrr?: (bidId: string, e?: React.MouseEvent) => void;
}

export default function BidRow({ bid, onEditIrr }: BidRowProps) {

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: bid.unit === 'TOTAL' || bid.unit === 'YEAR' ? 0 : 3,
            maximumFractionDigits: bid.unit === 'TOTAL' || bid.unit === 'YEAR' ? 0 : 3
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatIrr = (irr: number | null) => {
        if (irr === null || irr === undefined) return 'TBD';
        return `${(irr * 100).toFixed(2)}%`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING':
                return '#FBBD08';
            case 'ACCEPTED':
                return '#B5CC18';
            case 'REJECTED':
                return '#E4E4E4';
            default:
                return 'grey';
        }
    };

    return (
        <Table.Row>
            <Table.Cell>
                <Link to={`/project/${bid.project?.parentId}`}>
                    {bid.project?.name || 'Unknown Project'}
                </Link>
            </Table.Cell>
            <Table.Cell>
                {bid.project?.portfolio ? (
                    <Link to={`/portfolio/${bid.project.portfolio.parentId}`}>
                        {bid.project.portfolio.name}
                    </Link>
                ) : (
                    '-'
                )}
            </Table.Cell>
            <Table.Cell>
                {bid.investor?.company?.name || 'Unknown Investor'}
            </Table.Cell>
            <Table.Cell textAlign="right">
                {formatAmount(bid.amount)}
            </Table.Cell>
            <Table.Cell>
                {bid.unit}
            </Table.Cell>
            <Table.Cell
                textAlign="right"
                onClick={(e: React.MouseEvent) => onEditIrr?.(bid.id, e)}
                style={onEditIrr ? { cursor: 'pointer' } : {}}
                className={onEditIrr ? 'bid-irr-cell' : ''}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {onEditIrr && (
                        <Icon
                            name="edit"
                            className="bid-irr-icon"
                            style={{ margin: '-8px 4px 0 0' }}
                        />
                    )}
                    {formatIrr(bid.manualIrr ? bid.manualIrr : bid.calculatedIrr ?? null)}

                </div>
            </Table.Cell>
            <Table.Cell textAlign="center">
                <span style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '0.25rem',
                    backgroundColor: getStatusColor(bid.bidStatus),
                    color: 'black',
                    fontSize: '12px',
                    fontWeight: '400'
                }}>
                    {bid.bidStatus.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
            </Table.Cell>
            <Table.Cell textAlign="right">
                {formatDate(bid.createdAt)}
            </Table.Cell>
        </Table.Row>
    );
}
